/**
 * src/common/settings.js
 * Shared settings manager for Desktop App and Word Add-in
 * Handles LLM model selection per phase and congruence method configuration
 * 
 * Settings are persisted to:
 * - Local SQLite database (via API) when connected to desktop app
 * - localStorage as fallback for web-only mode
 */

import { safeFetchJson, getClientId } from "./api.js";

const SETTINGS_STORAGE_KEY = 'pelaikan_settings';

// Model every phase falls back to when a user is EU-coded / ZDR-restricted.
// Present in all phase dropdowns and a safe, broadly-capable Gemini default.
// Served on the Vertex EU multi-region endpoint (2.5 is not).
const ZDR_DEFAULT_MODEL = 'gemini:gemini-3.5-flash-lite';

// Per-phase overrides of ZDR_DEFAULT_MODEL. The file picker needs a stronger
// model than 2.5 Flash to reason over compilations and filename aliases.
// Mirrors ZDR_FILE_PICKER_MODEL in footcheck/resolve/agent_picker.py.
const ZDR_PHASE_DEFAULTS = {
  filePicker: 'gemini:gemini-3.8-flash'
};

function zdrDefaultFor(phase) {
  return ZDR_PHASE_DEFAULTS[phase] || ZDR_DEFAULT_MODEL;
}

// True when a model identifier belongs to Google Gemini (the only provider
// permitted for ZDR-restricted users for now).
function isGeminiModel(value) {
  return typeof value === 'string' && value.startsWith('gemini:');
}

// Default settings
const DEFAULT_SETTINGS = {
  llm: {
    parsing: 'default',
    resolving: 'default',
    filePicker: 'default',
    suggestion: 'default'  // Word Add-in only
  },
  congruenceMethod: 'micro_tasks',  // micro_tasks is the default (better accuracy)
  ocr: {
    overrideEnabled: false,
    provider: 'mistral',
    routeMode: 'direct',
    model: 'mistral-ocr-4-1',
    location: ''
  },
  history: {
    enabled: true
  }
};

/**
 * Convert API format to internal format
 * API uses: { llm_parsing: "model", llm_resolving: "model", congruence_method: "method" }
 * Internal uses: { llm: { parsing: "model", ... }, congruenceMethod: "method" }
 */
function fromApiFormat(apiSettings) {
  // Migration: 'standard' was the old default, upgrade to 'micro_tasks'
  let congruenceMethod = apiSettings.congruence_method || 'micro_tasks';
  if (congruenceMethod === 'standard') {
    console.log('[Settings] Migrating API congruence method from standard to micro_tasks');
    congruenceMethod = 'micro_tasks';
  }
  
  return {
    llm: {
      parsing: apiSettings.llm_parsing || 'default',
      resolving: apiSettings.llm_resolving || 'default',
      filePicker: apiSettings.llm_file_picker || 'default',
      suggestion: apiSettings.llm_suggestion || 'default'
    },
    congruenceMethod,
    ocr: {
      overrideEnabled: String(apiSettings.ocr_override_enabled || 'false').toLowerCase() === 'true',
      provider: apiSettings.ocr_provider || 'mistral',
      routeMode: apiSettings.ocr_route_mode || 'direct',
      model: apiSettings.ocr_model || 'mistral-ocr-4-1',
      location: apiSettings.ocr_location || ''
    },
    history: {
      enabled: String(apiSettings.history_enabled ?? 'true').toLowerCase() !== 'false'
    }
  };
}

/**
 * Convert internal format to API format
 */
function toApiFormat(settings) {
  return {
    llm_parsing: settings.llm.parsing,
    llm_resolving: settings.llm.resolving,
    llm_file_picker: settings.llm.filePicker,
    llm_suggestion: settings.llm.suggestion,
    congruence_method: settings.congruenceMethod,
    ocr_override_enabled: settings.ocr.overrideEnabled ? 'true' : 'false',
    ocr_provider: settings.ocr.provider,
    ocr_route_mode: settings.ocr.routeMode,
    ocr_model: settings.ocr.model,
    ocr_location: settings.ocr.location || '',
    history_enabled: settings.history?.enabled === false ? 'false' : 'true'
  };
}

/**
 * SettingsManager - Manages user preferences for LLM models and analysis options
 */
export class SettingsManager {
  constructor() {
    this.settings = this.loadSettingsFromStorage();
    this.listeners = [];
    this.apiBase = null;
    this.getAccessTokenFn = null;
    this._initialized = false;
    this._loading = false;
    // EU-coded / Zero-Data-Retention users are restricted to Gemini models.
    // Driven from a Firebase custom claim by the hosting context (see
    // setProviderRestriction). Defaults to false so non-EU users are unaffected.
    this.zdrRestricted = false;
  }

  /**
   * Whether this user is limited to Gemini models (EU-coded / ZDR).
   */
  isProviderRestricted() {
    return !!this.zdrRestricted;
  }

  /**
   * Apply (or lift) the EU/ZDR Gemini-only restriction. Called by the hosting
   * context once the user's Firebase claims are known. When restricted, any
   * non-Gemini stored choice is coerced to a safe Gemini default so that even
   * users who never open the Options pane never route to another provider.
   * @param {boolean} restricted
   */
  setProviderRestriction(restricted) {
    this.zdrRestricted = !!restricted;
    if (this.zdrRestricted) {
      this._coerceSettingsToGemini();
    }
    // Reflect immediately in any live Options UI.
    this.applyProviderRestrictionToUI();
  }

  /**
   * Force every LLM phase and OCR provider onto Gemini in the in-memory
   * settings. No-op for models that are already Gemini.
   */
  _coerceSettingsToGemini() {
    ['parsing', 'resolving', 'filePicker', 'suggestion'].forEach(phase => {
      const current = this.settings.llm[phase];
      // A stored ZDR_DEFAULT_MODEL on a phase with its own ZDR default is the
      // residue of the earlier blanket coercion, not a user choice: upgrade it.
      if (!isGeminiModel(current)
          || (current === ZDR_DEFAULT_MODEL && zdrDefaultFor(phase) !== ZDR_DEFAULT_MODEL)) {
        this.settings.llm[phase] = zdrDefaultFor(phase);
      }
    });
    // Local PyMuPDF extraction does not transmit document data and is safe
    // under ZDR. Only cloud OCR providers must be coerced to Gemini.
    if (this.settings.ocr.provider !== 'gemini' && this.settings.ocr.provider !== 'local') {
      this.settings.ocr.provider = 'gemini';
      // The default OCR model id is Mistral-specific; clear it so the Gemini
      // server default applies rather than a stale mistral-* identifier.
      if (/mistral/i.test(this.settings.ocr.model || '')) {
        this.settings.ocr.model = '';
      }
    }
  }

  /**
   * Initialize with API connection (called when app starts)
   */
  async initialize(apiBase = null, getAccessTokenFn = null) {
    if (this._initialized) return;
    
    this.apiBase = apiBase || window.API_BASE;
    this.getAccessTokenFn = getAccessTokenFn;
    
    // Try to load from API (local database)
    await this.loadSettingsFromApi();
    this._initialized = true;
  }

  /**
   * Load settings from localStorage (fallback/cache)
   * @returns {Object} The loaded settings or defaults
   */
  loadSettingsFromStorage() {
    try {
      const stored = localStorage.getItem(SETTINGS_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Merge with defaults to ensure all keys exist
        const merged = this.mergeWithDefaults(parsed);
        
        // Migration: 'standard' was the old default, upgrade to 'micro_tasks'
        // This ensures users who had 'standard' from before get the new default
        if (merged.congruenceMethod === 'standard') {
          console.log('[Settings] Migrating congruence method from standard to micro_tasks');
          merged.congruenceMethod = 'micro_tasks';
          // Save the migrated settings
          try {
            localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(merged));
          } catch (e) {
            // Ignore save errors during migration
          }
        }
        
        return merged;
      }
    } catch (e) {
      console.warn('[Settings] Failed to load settings from storage:', e);
    }
    return {
      ...DEFAULT_SETTINGS,
      llm: { ...DEFAULT_SETTINGS.llm },
      ocr: { ...DEFAULT_SETTINGS.ocr },
      history: { ...DEFAULT_SETTINGS.history }
    };
  }

  /**
   * Load settings from API (local database via bridge)
   */
  async loadSettingsFromApi() {
    if (this._loading) return;
    this._loading = true;
    
    try {
      const base = this.apiBase || window.API_BASE;
      const clientId = getClientId();
      
      // Build URL with client_id for bridge mode
      let url = `${base}/db/settings`;
      if (clientId) {
        url += `?client_id=${encodeURIComponent(clientId)}`;
      }
      
      // Get access token if available
      let headers = {};
      if (this.getAccessTokenFn) {
        try {
          const token = await this.getAccessTokenFn();
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
        } catch (e) {
          console.warn('[Settings] Could not get access token:', e);
        }
      }
      
      console.log('[Settings] Loading from API:', url);
      const response = await safeFetchJson(url, { headers });
      
      if (response && response.settings) {
        console.log('[Settings] Loaded from API:', response.settings);
        this.settings = fromApiFormat(response.settings);
        // Re-apply the Gemini-only restriction: the API may return non-Gemini
        // choices saved from another device or before the account was EU-coded.
        if (this.zdrRestricted) this._coerceSettingsToGemini();
        // Also cache in localStorage
        this.saveSettingsToStorage();
        this.notifyListeners();
      }
    } catch (e) {
      console.warn('[Settings] Failed to load from API, using localStorage:', e);
      // Settings already loaded from localStorage in constructor
    } finally {
      this._loading = false;
    }
  }

  /**
   * Save settings to localStorage (cache)
   */
  saveSettingsToStorage() {
    try {
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(this.settings));
    } catch (e) {
      console.warn('[Settings] Failed to save to localStorage:', e);
    }
  }

  /**
   * Save settings to API (local database via bridge)
   */
  async saveSettingsToApi() {
    try {
      const base = this.apiBase || window.API_BASE;
      const clientId = getClientId();
      
      const url = `${base}/db/settings`;
      
      // Get access token if available
      let headers = {
        'Content-Type': 'application/json'
      };
      if (this.getAccessTokenFn) {
        try {
          const token = await this.getAccessTokenFn();
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
        } catch (e) {
          console.warn('[Settings] Could not get access token:', e);
        }
      }
      
      const body = {
        client_id: clientId,
        settings: toApiFormat(this.settings)
      };
      
      console.log('[Settings] Saving to API:', body);
      const response = await safeFetchJson(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
      });
      
      if (response && response.settings) {
        console.log('[Settings] Saved to API successfully');
        return true;
      }
      return false;
    } catch (e) {
      console.warn('[Settings] Failed to save to API:', e);
      return false;
    }
  }

  /**
   * Merge loaded settings with defaults to ensure all keys exist
   * @param {Object} loaded - The loaded settings
   * @returns {Object} Merged settings
   */
  mergeWithDefaults(loaded) {
    return {
      llm: {
        ...DEFAULT_SETTINGS.llm,
        ...(loaded.llm || {})
      },
      congruenceMethod: loaded.congruenceMethod || DEFAULT_SETTINGS.congruenceMethod,
      ocr: {
        ...DEFAULT_SETTINGS.ocr,
        ...(loaded.ocr || {})
      },
      history: {
        ...DEFAULT_SETTINGS.history,
        ...(loaded.history || {})
      }
    };
  }

  /**
   * Save settings (both to API and localStorage)
   */
  async saveSettings() {
    try {
      // Always save to localStorage as cache
      this.saveSettingsToStorage();
      
      // Try to save to API
      const apiSuccess = await this.saveSettingsToApi();
      
      console.log('[Settings] Settings saved:', this.settings, 'API:', apiSuccess);
      this.notifyListeners();
      return true;
    } catch (e) {
      console.error('[Settings] Failed to save settings:', e);
      return false;
    }
  }

  /**
   * Reset settings to defaults
   */
  async resetToDefaults() {
    this.settings = {
      ...DEFAULT_SETTINGS,
      llm: { ...DEFAULT_SETTINGS.llm },
      ocr: { ...DEFAULT_SETTINGS.ocr },
      history: { ...DEFAULT_SETTINGS.history }
    };
    
    // Try to reset via API
    try {
      const base = this.apiBase || window.API_BASE;
      const clientId = getClientId();
      
      const url = `${base}/db/settings/reset`;
      
      let headers = {
        'Content-Type': 'application/json'
      };
      if (this.getAccessTokenFn) {
        try {
          const token = await this.getAccessTokenFn();
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
        } catch (e) {
          console.warn('[Settings] Could not get access token:', e);
        }
      }
      
      const body = clientId ? JSON.stringify({ client_id: clientId }) : '{}';
      
      await safeFetchJson(url, {
        method: 'POST',
        headers,
        body
      });
      console.log('[Settings] Reset via API');
    } catch (e) {
      console.warn('[Settings] Failed to reset via API:', e);
    }
    
    this.saveSettingsToStorage();
    this.syncUIFromSettings();
    this.notifyListeners();
    return true;
  }

  /**
   * Get LLM model for a specific phase
   * @param {string} phase - The phase ('parsing', 'resolving', 'filePicker', 'suggestion')
   * @returns {string} The model identifier or 'default'
   */
  getLlmForPhase(phase) {
    return this.settings.llm[phase] || 'default';
  }

  /**
   * Set LLM model for a specific phase
   * @param {string} phase - The phase
   * @param {string} model - The model identifier
   */
  setLlmForPhase(phase, model) {
    this.settings.llm[phase] = model;
  }

  /**
   * Get the congruence method
   * @returns {string} The congruence method ('standard' or 'micro_tasks')
   */
  getCongruenceMethod() {
    return this.settings.congruenceMethod;
  }

  /**
   * Set the congruence method
   * @param {string} method - The method identifier
   */
  setCongruenceMethod(method) {
    this.settings.congruenceMethod = method;
  }

  getOcrSettings() {
    return { ...this.settings.ocr };
  }

  setOcrOverrideEnabled(enabled) {
    this.settings.ocr.overrideEnabled = !!enabled;
  }

  setOcrProvider(provider) {
    this.settings.ocr.provider = provider || 'mistral';
    if (this.settings.ocr.provider === 'local') {
      this.settings.ocr.routeMode = 'direct';
      this.settings.ocr.model = 'pymupdf4llm';
    }
  }

  setOcrRouteMode(routeMode) {
    this.settings.ocr.routeMode = routeMode || 'direct';
  }

  setOcrModel(model) {
    this.settings.ocr.model = model || 'mistral-ocr-4-1';
  }

  setOcrLocation(location) {
    this.settings.ocr.location = location || '';
  }

  isHistoryEnabled() {
    return this.settings.history?.enabled !== false;
  }

  setHistoryEnabled(enabled) {
    if (!this.settings.history) this.settings.history = { ...DEFAULT_SETTINGS.history };
    this.settings.history.enabled = !!enabled;
  }

  /**
   * Get all settings as an object
   * @returns {Object} Current settings
   */
  getAllSettings() {
    return {
      ...this.settings,
      llm: { ...this.settings.llm },
      ocr: { ...this.settings.ocr },
      history: { ...this.settings.history }
    };
  }

  /**
   * Add a listener for settings changes
   * @param {Function} callback - Callback function
   */
  addListener(callback) {
    this.listeners.push(callback);
  }

  /**
   * Remove a listener
   * @param {Function} callback - Callback function to remove
   */
  removeListener(callback) {
    this.listeners = this.listeners.filter(l => l !== callback);
  }

  /**
   * Notify all listeners of settings changes
   */
  notifyListeners() {
    this.listeners.forEach(cb => {
      try {
        cb(this.settings);
      } catch (e) {
        console.error('[Settings] Listener error:', e);
      }
    });
  }

  /**
   * Initialize the settings UI and wire up event listeners
   * @param {Object} options - Options for initialization
   * @param {boolean} options.includeSuggestion - Whether to include suggestion LLM (Word Add-in)
   */
  async initializeSettingsUI(options = {}) {
    const { includeSuggestion = false } = options;

    // Make sure we've loaded from API first
    if (!this._initialized) {
      await this.initialize();
    } else {
      // Refresh from API to get latest
      await this.loadSettingsFromApi();
    }

    // Sync UI elements from current settings
    this.syncUIFromSettings();

    // Wire save button
    const btnSave = document.getElementById('btn-save-settings');
    if (btnSave) {
      // Remove existing listeners to avoid duplicates
      const newBtn = btnSave.cloneNode(true);
      btnSave.parentNode.replaceChild(newBtn, btnSave);
      
      newBtn.addEventListener('click', async () => {
        this.syncSettingsFromUI(includeSuggestion);
        const saved = await this.saveSettings();
        this.showStatus(saved ? 'Settings saved!' : 'Failed to save settings', saved ? 'success' : 'error');
        
        // Sync congruence method dropdown in Audit tab if it exists
        this.syncCongruenceMethodToAuditTab();
      });
    }

    // Wire reset button
    const btnReset = document.getElementById('btn-reset-settings');
    if (btnReset) {
      // Remove existing listeners to avoid duplicates
      const newBtn = btnReset.cloneNode(true);
      btnReset.parentNode.replaceChild(newBtn, btnReset);
      
      newBtn.addEventListener('click', async () => {
        if (confirm('Reset all settings to defaults?')) {
          await this.resetToDefaults();
          this.showStatus('Settings reset to defaults', 'success');
          
          // Sync congruence method dropdown in Audit tab
          this.syncCongruenceMethodToAuditTab();
        }
      });
    }

    // Wire change listeners to individual selects for real-time feedback
    const phases = ['parsing', 'resolving', 'filePicker'];
    if (includeSuggestion) phases.push('suggestion');

    phases.forEach(phase => {
      const select = document.getElementById(`settings-llm-${phase}`);
      if (select) {
        select.addEventListener('change', () => {
          this.setLlmForPhase(phase, select.value);
        });
      }
    });

    const congruenceSelect = document.getElementById('settings-congruence-method');
    if (congruenceSelect) {
      congruenceSelect.addEventListener('change', () => {
        this.setCongruenceMethod(congruenceSelect.value);
      });
    }

    const ocrOverrideCheckbox = document.getElementById('settings-ocr-override-enabled');
    if (ocrOverrideCheckbox) {
      ocrOverrideCheckbox.addEventListener('change', () => {
        this.setOcrOverrideEnabled(ocrOverrideCheckbox.checked);
        this.updateOcrUIState();
      });
    }

    const ocrProviderSelect = document.getElementById('settings-ocr-provider');
    if (ocrProviderSelect) {
      ocrProviderSelect.addEventListener('change', () => {
        this.setOcrProvider(ocrProviderSelect.value);
        this.updateOcrUIState();
      });
    }

    const ocrRouteModeSelect = document.getElementById('settings-ocr-route-mode');
    if (ocrRouteModeSelect) {
      ocrRouteModeSelect.addEventListener('change', () => {
        this.setOcrRouteMode(ocrRouteModeSelect.value);
        this.updateOcrUIState();
      });
    }

    const ocrModelInput = document.getElementById('settings-ocr-model');
    if (ocrModelInput) {
      ocrModelInput.addEventListener('change', () => {
        this.setOcrModel(ocrModelInput.value);
      });
    }

    const ocrLocationInput = document.getElementById('settings-ocr-location');
    if (ocrLocationInput) {
      ocrLocationInput.addEventListener('change', () => {
        this.setOcrLocation(ocrLocationInput.value);
      });
    }

    const historyEnabledCheckbox = document.getElementById('settings-history-enabled');
    if (historyEnabledCheckbox) {
      historyEnabledCheckbox.addEventListener('change', () => {
        this.setHistoryEnabled(historyEnabledCheckbox.checked);
      });
    }

    // Listen for changes from the Audit tab congruence dropdown
    const auditCongruenceSelect = document.getElementById('congruence-method');
    if (auditCongruenceSelect) {
      auditCongruenceSelect.addEventListener('change', async () => {
        this.setCongruenceMethod(auditCongruenceSelect.value);
        await this.saveSettings();
        // Sync back to settings tab
        if (congruenceSelect && congruenceSelect.value !== auditCongruenceSelect.value) {
          congruenceSelect.value = auditCongruenceSelect.value;
        }
      });
    }
  }

  /**
   * Sync UI elements from current settings
   */
  syncUIFromSettings() {
    const phases = ['parsing', 'resolving', 'filePicker', 'suggestion'];
    
    phases.forEach(phase => {
      const select = document.getElementById(`settings-llm-${phase}`);
      if (select) {
        select.value = this.getLlmForPhase(phase);
      }
    });

    const congruenceSelect = document.getElementById('settings-congruence-method');
    if (congruenceSelect) {
      congruenceSelect.value = this.getCongruenceMethod();
    }

    const ocr = this.getOcrSettings();
    const ocrOverrideCheckbox = document.getElementById('settings-ocr-override-enabled');
    if (ocrOverrideCheckbox) {
      ocrOverrideCheckbox.checked = !!ocr.overrideEnabled;
    }

    const ocrProviderSelect = document.getElementById('settings-ocr-provider');
    if (ocrProviderSelect) {
      ocrProviderSelect.value = ocr.provider;
    }

    const ocrRouteModeSelect = document.getElementById('settings-ocr-route-mode');
    if (ocrRouteModeSelect) {
      ocrRouteModeSelect.value = ocr.routeMode;
    }

    const ocrModelInput = document.getElementById('settings-ocr-model');
    if (ocrModelInput) {
      ocrModelInput.value = ocr.model;
    }

    const ocrLocationInput = document.getElementById('settings-ocr-location');
    if (ocrLocationInput) {
      ocrLocationInput.value = ocr.location || '';
    }

    const historyEnabledCheckbox = document.getElementById('settings-history-enabled');
    if (historyEnabledCheckbox) {
      historyEnabledCheckbox.checked = this.isHistoryEnabled();
    }

    this.updateOcrUIState();

    // Enforce the Gemini-only restriction on the freshly-populated controls.
    this.applyProviderRestrictionToUI();

    // Also sync the Audit tab congruence dropdown
    this.syncCongruenceMethodToAuditTab();
  }

  /**
   * Show/hide non-Gemini choices in the Options pane based on the ZDR
   * restriction. When restricted, OpenAI/Mistral optgroups and the "Default
   * (Auto)" option are hidden (Auto could route to a non-Gemini provider
   * server-side), and the OCR provider is locked to Gemini. When not
   * restricted, every choice is restored. Safe to call when the pane is not
   * in the DOM (each lookup no-ops).
   */
  applyProviderRestrictionToUI() {
    const restricted = !!this.zdrRestricted;

    ['parsing', 'resolving', 'filePicker', 'suggestion'].forEach(phase => {
      const select = document.getElementById(`settings-llm-${phase}`);
      if (!select) return;

      select.querySelectorAll('optgroup').forEach(group => {
        const isGeminiGroup = /gemini/i.test(group.label);
        const hide = restricted && !isGeminiGroup;
        group.hidden = hide;
        group.disabled = hide;
      });

      Array.from(select.options).forEach(opt => {
        // Hide "Default (Auto)" too — server auto-routing may pick a
        // non-Gemini provider, which would violate ZDR.
        const hide = restricted && !isGeminiModel(opt.value);
        opt.hidden = hide;
        opt.disabled = hide;
      });

      // If the current selection is no longer permitted, coerce it.
      if (restricted && !isGeminiModel(select.value)) {
        select.value = zdrDefaultFor(phase);
        this.setLlmForPhase(phase, zdrDefaultFor(phase));
      }
    });

    // OCR provider: local extraction is allowed because it does not transmit
    // document data. Cloud OCR remains restricted to Gemini.
    const ocrProvider = document.getElementById('settings-ocr-provider');
    if (ocrProvider) {
      Array.from(ocrProvider.options).forEach(opt => {
        const hide = restricted && opt.value !== 'gemini' && opt.value !== 'local';
        opt.hidden = hide;
        opt.disabled = hide;
      });
      if (restricted && ocrProvider.value !== 'gemini' && ocrProvider.value !== 'local') {
        ocrProvider.value = 'gemini';
        this.setOcrProvider('gemini');
      }
    }

    // Toggle the explanatory banner if present.
    const banner = document.getElementById('settings-zdr-banner');
    if (banner) banner.style.display = restricted ? '' : 'none';
  }

  /**
   * Sync settings from UI elements
   * @param {boolean} includeSuggestion - Whether to include suggestion LLM
   */
  syncSettingsFromUI(includeSuggestion = false) {
    const phases = ['parsing', 'resolving', 'filePicker'];
    if (includeSuggestion) phases.push('suggestion');

    phases.forEach(phase => {
      const select = document.getElementById(`settings-llm-${phase}`);
      if (select) {
        this.setLlmForPhase(phase, select.value);
      }
    });

    const congruenceSelect = document.getElementById('settings-congruence-method');
    if (congruenceSelect) {
      this.setCongruenceMethod(congruenceSelect.value);
    }

    const ocrOverrideCheckbox = document.getElementById('settings-ocr-override-enabled');
    if (ocrOverrideCheckbox) {
      this.setOcrOverrideEnabled(ocrOverrideCheckbox.checked);
    }

    const ocrProviderSelect = document.getElementById('settings-ocr-provider');
    if (ocrProviderSelect) {
      this.setOcrProvider(ocrProviderSelect.value);
    }

    const ocrRouteModeSelect = document.getElementById('settings-ocr-route-mode');
    if (ocrRouteModeSelect) {
      this.setOcrRouteMode(ocrRouteModeSelect.value);
    }

    const ocrModelInput = document.getElementById('settings-ocr-model');
    if (ocrModelInput) {
      this.setOcrModel(ocrModelInput.value);
    }

    const ocrLocationInput = document.getElementById('settings-ocr-location');
    if (ocrLocationInput) {
      this.setOcrLocation(ocrLocationInput.value);
    }

    const historyEnabledCheckbox = document.getElementById('settings-history-enabled');
    if (historyEnabledCheckbox) {
      this.setHistoryEnabled(historyEnabledCheckbox.checked);
    }
  }

  updateOcrUIState() {
    const overrideEnabled = !!this.settings.ocr.overrideEnabled;
    const routeMode = this.settings.ocr.routeMode || 'direct';

    const overrideFields = document.querySelectorAll('[data-ocr-override-field="true"]');
    overrideFields.forEach(el => {
      el.disabled = !overrideEnabled;
    });

    const locationInput = document.getElementById('settings-ocr-location');
    if (locationInput) {
      locationInput.disabled = !overrideEnabled || routeMode !== 'vertex';
    }

    const providerSelect = document.getElementById('settings-ocr-provider');
    const routeModeSelect = document.getElementById('settings-ocr-route-mode');
    const localProvider = providerSelect?.value === 'local' || this.settings.ocr.provider === 'local';
    if (routeModeSelect) {
      const vertexOption = routeModeSelect.querySelector('option[value="vertex"]');
      if (vertexOption) vertexOption.disabled = localProvider;
      if (localProvider && routeModeSelect.value !== 'direct') {
        routeModeSelect.value = 'direct';
        this.setOcrRouteMode('direct');
      }
    }
  }

  /**
   * Sync congruence method to the Audit tab dropdown (if it exists)
   */
  syncCongruenceMethodToAuditTab() {
    const auditCongruenceSelect = document.getElementById('congruence-method');
    if (auditCongruenceSelect) {
      auditCongruenceSelect.value = this.getCongruenceMethod();
    }
  }

  /**
   * Show a status message in the settings tab
   * @param {string} message - The message to show
   * @param {string} type - 'success' or 'error'
   */
  showStatus(message, type = 'success') {
    const statusEl = document.getElementById('settings-status');
    if (statusEl) {
      statusEl.textContent = message;
      statusEl.className = `settings-status ${type}`;
      
      // Clear after 3 seconds
      setTimeout(() => {
        statusEl.textContent = '';
        statusEl.className = 'settings-status';
      }, 3000);
    }
  }
}

// Export a singleton instance for easy access
export const settingsManager = new SettingsManager();

// Export default settings for reference
export { DEFAULT_SETTINGS };

/**
 * Get LLM model settings formatted for API payload
 * Returns only non-default settings to allow server defaults to apply
 * @returns {Object} Object with llm_model_* keys for non-default phases
 */
export function getSettingsForApiPayload() {
  const payload = {};
  const settings = settingsManager.getAllSettings();
  const restricted = settingsManager.isProviderRestricted();

  // For EU/ZDR users, never let a non-Gemini choice (or server auto-routing via
  // 'default') reach the API — coerce to the Gemini default instead.
  const forPhase = (value, phase) => {
    if (restricted && !isGeminiModel(value)) return zdrDefaultFor(phase);
    return value;
  };

  // Only include LLM models that are not set to 'default'
  // This allows the server to use its own defaults when not overridden
  const parsing = forPhase(settings.llm.parsing, 'parsing');
  if (parsing && parsing !== 'default') {
    payload.llm_model_parsing = parsing;
  }
  const resolving = forPhase(settings.llm.resolving, 'resolving');
  if (resolving && resolving !== 'default') {
    payload.llm_model_resolving = resolving;
  }
  const filePicker = forPhase(settings.llm.filePicker, 'filePicker');
  if (filePicker && filePicker !== 'default') {
    payload.llm_model_file_picker = filePicker;
  }
  const suggestion = forPhase(settings.llm.suggestion, 'suggestion');
  if (suggestion && suggestion !== 'default') {
    payload.llm_model_suggestion = suggestion;
  }
  
  // Congruence method is always included (has a valid value)
  payload.congruence_method = settings.congruenceMethod;
  
  return payload;
}

/**
 * Make settings manager available globally for easy access
 * This allows other parts of the app to access settings without importing
 */
if (typeof window !== 'undefined') {
  window.footcheckSettingsManager = settingsManager;
  window.getFootcheckSettingsForApi = getSettingsForApiPayload;
  
  // Sync saved settings to DOM elements when the page loads
  // This ensures the Audit tab congruence dropdown reflects saved settings
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      settingsManager.syncCongruenceMethodToAuditTab();
    });
  } else {
    // DOM already loaded, sync immediately
    settingsManager.syncCongruenceMethodToAuditTab();
  }
}
